import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

const THEME_KEY = 'quantrolix-theme';

const THEME_VARS = {
  dark: {
    '--bg-primary': '#0f1419',
    '--bg-secondary': 'rgba(10, 10, 15, 0.95)',
    '--bg-card': 'rgba(10, 10, 15, 0.6)',
    '--bg-input': 'rgba(102, 126, 234, 0.1)',
    '--bg-hover': 'rgba(102, 126, 234, 0.15)',
    '--border-primary': 'rgba(102, 126, 234, 0.2)',
    '--border-input': 'rgba(102, 126, 234, 0.3)',
    '--border-accent': 'rgba(102, 126, 234, 0.5)',
    '--text-primary': '#ffffff',
    '--text-secondary': 'rgba(255, 255, 255, 0.7)',
    '--text-muted': 'rgba(255, 255, 255, 0.5)',
    '--text-accent': '#00d4ff',
    '--gradient-primary': 'linear-gradient(135deg, #667eea, #00d4ff)',
    '--gradient-bg': 'radial-gradient(120% 120% at 50% 20%, rgba(35, 40, 70, 0.95) 0%, #05060d 100%)',
    '--shadow-glow': '0 0 30px rgba(102, 126, 234, 0.3)',
    '--backdrop-blur': 'blur(10px)',
  },
  light: {
    '--bg-primary': '#f0f2f5',
    '--bg-secondary': 'rgba(255, 255, 255, 0.95)',
    '--bg-card': 'rgba(255, 255, 255, 0.85)',
    '--bg-input': 'rgba(102, 126, 234, 0.06)',
    '--bg-hover': 'rgba(102, 126, 234, 0.08)',
    '--border-primary': 'rgba(102, 126, 234, 0.15)',
    '--border-input': 'rgba(102, 126, 234, 0.25)',
    '--border-accent': 'rgba(102, 126, 234, 0.4)',
    '--text-primary': '#1a1a2e',
    '--text-secondary': 'rgba(26, 26, 46, 0.75)',
    '--text-muted': 'rgba(26, 26, 46, 0.5)',
    '--text-accent': '#4a6cf7',
    '--gradient-primary': 'linear-gradient(135deg, #667eea, #4a6cf7)',
    '--gradient-bg': 'linear-gradient(135deg, #e8ecf4 0%, #dde1e9 50%, #d0d5e0 100%)',
    '--shadow-glow': '0 4px 20px rgba(102, 126, 234, 0.15)',
    '--backdrop-blur': 'blur(10px)',
  },
};

function getSystemTheme() {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function resolveTheme(preference) {
  if (preference === 'system') return getSystemTheme();
  return preference;
}

export function ThemeProvider({ children }) {
  const [themePreference, setThemePreference] = useState(() => {
    try {
      return localStorage.getItem(THEME_KEY) || 'dark';
    } catch {
      return 'dark';
    }
  });

  const resolvedTheme = resolveTheme(themePreference);

  const applyTheme = useCallback((theme) => {
    const vars = THEME_VARS[theme] || THEME_VARS.dark;
    const root = document.documentElement;

    // Enable smooth transition
    root.style.setProperty('transition', 'background-color 0.3s ease, color 0.3s ease');

    Object.entries(vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    root.setAttribute('data-theme', theme);
    document.body.style.background = vars['--bg-primary'];
    document.body.style.color = vars['--text-primary'];
  }, []);

  // Apply theme whenever resolved theme changes
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme, applyTheme]);

  // Listen for system theme changes when preference is 'system'
  useEffect(() => {
    if (themePreference !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => applyTheme(getSystemTheme());
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [themePreference, applyTheme]);

  const setTheme = useCallback((newPreference) => {
    setThemePreference(newPreference);
    try {
      localStorage.setItem(THEME_KEY, newPreference);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const value = {
    themePreference,
    resolvedTheme,
    setTheme,
    isDark: resolvedTheme === 'dark',
    isLight: resolvedTheme === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
